/**
 * Typed fetch client for the Corenote API.
 *
 * - Same-origin only: every path is relative ('/auth...', '/api...').
 * - `credentials: 'include'` on every call (cookie session).
 * - Mutating methods attach `X-CSRF-Token` from the injected token provider.
 * - `fetch` is injectable so tests can stub the transport.
 * - A transient `unavailable` (HTTP 503, or a body whose code is
 *   `unavailable`) is retried for reads and for writes that already
 *   carry an idempotency key. Three attempts, about 0.5s doubling to
 *   4s, with ±20% jitter. A write without a key is never replayed.
 */

import type {
  ActivityDto,
  ActivityFeedDto,
  TaskTriageProposal,
  DedupeJudgeSummary,
  DedupeJudgementList,
  DedupeJudgementStatus,
  WorkspaceDedupePolicy,
  ReviewQueue,
  TaskDecideEnrichments,
  GoalDecideEnrichments,
  SetTaskRiskClassRequest,
  TaskRiskClass,
  TaskApprovalList,
  TaskApprovalRequest,
  DecideTaskApprovalRequest,
  DependencyInferenceSummary,
  DependencyProposal,
  DependencyProposalList,
  DependencyProposalStatus,
  AcceptedProposal,
  RelatedTasks,
  WorkspaceBlockers,
  SetWorkspaceComposeSettingRequest,
  TranscribeResultDto,
  UndoComposeRequest,
  WorkspaceComposeSettingDto,
  AckInboxRequest,
  InboxAckDto,
  InboxDto,
  SetWatchRequest,
  WatchDto,
  WatchListDto,
  AddTaskDevEvidenceRequest,
  BacklinkDto,
  VerifiedEditionResponse,
  VerificationDto,
  VerifyPageRequest,
  WithdrawVerificationRequest,
  AcceptPageShareRequest,
  AccessRequestDto,
  CreateAccessRequestRequest,
  CreatePageShareRequest,
  DecideAccessRequestRequest,
  GuestDto,
  PageShareAcceptedDto,
  PageShareDto,
  AgentConnectionCreatedDto,
  AgentConnectionDto,
  ApplyTemplateRequest,
  ApplyPageTemplateRequest,
  ApplyRowTemplateRequest,
  AttachmentDto,
  BoardViewDto,
  BulkTasksRequest,
  BulkTasksResponse,
  CarryoverReportDto,
  ClaimRequest,
  CloseCycleResponseDto,
  CommentDto,
  ComposeTaskRequest,
  ComposeResultDto,
  UndoComposeReceiptDto,
  CompleteRequest,
  CreateAgentConnectionRequest,
  CreateBoardViewRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  CreateCycleRequest,
  CreateInvitationRequest,
  CreateLabelRequest,
  CreateReleaseRequest,
  CreatePageFromMarkdownRequest,
  CreatePageRequest,
  CreatePageTemplateFromPageRequest,
  CreateTaskRequest,
  CreateWorkTypeRequest,
  CrdtSavedDto,
  ExecutionStatus,
  CrdtConflictDto,
  CrdtStateDto,
  CrdtUpdateRequest,
  PresencePublishRequest,
  CycleDto,
  CycleReportDto,
  CycleSnapshotDto,
  DevEvidenceDto,
  EmailSentAck,
  EmailVerificationConfirmRequest,
  EmailVerificationResultDto,
  ErrorDto,
  ErrorCode,
  InvitationAcceptedDto,
  InvitationDto,
  InvitationViewDto,
  LabelDto,
  LeaseDto,
  LoginRequest,
  MilestoneDto,
  PageDto,
  PageSummaryDto,
  PasswordChangedAck,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  ProfileDto,
  ProjectDto,
  UpdateProjectRequest,
  InitiativeSummaryDto,
  InitiativeDto,
  CreateInitiativeRequest,
  UpdateInitiativeRequest,
  ArchiveInitiativeRequest,
  AttachInitiativeProjectRequest,
  ConnectorConnectionDto,
  ConnectorSyncRunDto,
  ConnectorWorkspaceSettingsDto,
  CreateIntegrationEndpointRequest,
  IntegrationEndpointDto,
  IntakeDeliveryListDto,
  IntegrationEndpointListDto,
  IntegrationEndpointSecretDto,
  RotateIntegrationEndpointSecretRequest,
  SetIntegrationEndpointStatusRequest,
  CreateConnectorConnectionRequest,
  DetachInitiativeProjectRequest,
  ReorderInitiativeProjectRequest,
  GoalDto,
  CreateGoalRequest,
  UpdateGoalRequest,
  DeleteGoalRequest,
  GoalOwnerKind,
  LinearImportRunDto,
  LinearPushRunDto,
  LinearReconcileRunDto,
  LinearTeamMappingsDto,
  RevokeConnectorConnectionRequest,
  SetConnectorWorkspaceSettingsRequest,
  SetLinearTeamMappingsRequest,
  TaskLinkedConnectorItemsDto,
  TriggerConnectorSyncRequest,
  TriggerLinearImportRequest,
  TriggerLinearPushRequest,
  TriggerLinearReconcileRequest,
  UpdateConnectorConnectionRequest,
  PageTemplateDto,
  RowTemplateDto,
  CreatePageTemplateRequest,
  UpdatePageTemplateRequest,
  ArchivePageTemplateRequest,
  CreateRowTemplateRequest,
  UpdateRowTemplateRequest,
  ArchiveRowTemplateRequest,
  DatabaseRowDto,
  RegisterRequest,
  ReleaseDto,
  ReleaseItemDto,
  ReleaseReportDto,
  RestorePageRequest,
  ReviewRequest,
  ReviewRequestNotificationRequest,
  ReviewRequestReceiptDto,
  SavedDto,
  SearchParams,
  SearchResponse,
  SessionDto,
  ReviewPolicyDto,
  SetReviewPolicyRequest,
  SetReviewPolicyReceiptDto,
  UsageAttributionRowDto,
  UpdateProfileRequest,
  SetCycleStatusRequest,
  SetTaskCycleRequest,
  SetTaskDataSourceRequest,
  SetTaskLabelsRequest,
  SetTaskWorkTypeRequest,
  TaskDto,
  TaskCompactListPage,
  TaskListPage,
  TaskCompletionDto,
  AddTaskDependencyRequest,
  TaskDependencyDto,
  TaskBlockerDto,
  TemplateDto,
  TransitionRequest,
  UpdateLabelRequest,
  UpdateTaskRequest,
  SetTaskPriorityRequest,
  UpdatePageRequest,
  UpdateWorkTypeRequest,
  WorkTypeDto,
  WorkTypeRevisionDto,
  WorkspaceDto,
  MovePageRequest,
  PageDiffDto,
  PageLockDto,
  PageExportResult,
  PagePathDto,
  ProjectHomeDto,
  RestoreFromTrashRequest,
  RevisionDto,
  RevisionSummaryDto,
  CommentThreadStateDto,
  MentionablesDto,
  NotificationPreferencesDto,
  NotificationsDto,
  NotificationReadAllDto,
  NotificationReadDto,
  UpdateNotificationPreferencesRequest,
  TrashEntryDto,
  TrashPageRequest,
  LockPageRequest,
} from "./types.js";

export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export interface ClientOptions {
  fetchImpl?: FetchLike;
  /** Returns the current CSRF token (from the session DTO). */
  csrfToken?: () => string | undefined;
  /** CORE-389: per-request ceiling in ms (positive; defaults to 30s).
   * A stuck call rejects with a `TimeoutError` DOMException instead of
   * spinning forever. */
  timeoutMs?: number;
}

/** Error thrown for any non-2xx response; carries the stable wire code. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId?: string;
  /** Structured dependency-blocker list; present only with
   *  `dependency_blocked` (CORE-25). */
  readonly blockers?: TaskBlockerDto[];
  /** Static validation detail; present only with `validation` — the closed
   *  vocabulary the caller must use (CORE-62 actionable-400 rule). */
  readonly detail?: string;

  constructor(
    code: ErrorCode,
    status: number,
    requestId?: string,
    blockers?: TaskBlockerDto[],
    detail?: string,
  ) {
    super(
      detail
        ? `corenote: ${code} (${status}): ${detail}`
        : `corenote: ${code} (${status})`,
    );
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.blockers = blockers;
    this.detail = detail;
  }
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** CORE-389: every typed request gets a ceiling so a wedged socket or
 * hung server surfaces as an error (toast) instead of an eternal
 * spinner. Generous against real mutation latencies (durable writes
 * land in ~2s; imports are checkpointed and return receipts fast). */
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/** Composes the caller's abort signal with the timeout deadline using
 * the platform signals (they carry the right rejection reasons
 * natively: `TimeoutError` for the deadline, the caller's own reason
 * for cancels). Engines without AbortSignal.timeout/any (Safari
 * < 17.4) keep the caller signal alone — degraded, never broken. */
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number) {
  const timeout = AbortSignal.timeout?.(timeoutMs);
  return timeout
    ? (AbortSignal.any?.([signal ?? timeout, timeout]) ?? timeout)
    : signal;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

export class CorenoteClient {
  private readonly doFetch: FetchLike;
  private readonly csrfToken: () => string | undefined;
  /** CORE-389: default ceiling for every typed request. */
  private readonly timeoutMs: number;
  /** Live token, updated by the session layer (setCsrfToken); wins over
   *  the constructor provider so React never needs a ref-backed getter. */
  private currentCsrfToken?: string;
  private retired = false;

  constructor(options: ClientOptions = {}) {
    this.doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.csrfToken = options.csrfToken ?? (() => undefined);
    this.timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  }

  /** Adopt/revoke the session's CSRF token (login, getSession, logout). */
  setCsrfToken(token: string | undefined): void {
    if (this.retired) return;
    this.currentCsrfToken = token;
  }

  /** Permanently close this authentication scope to future dispatch.
   * Requests already handed to fetch settle normally; they are not replayed. */
  retire(): void {
    this.retired = true;
    this.currentCsrfToken = undefined;
  }

  /** Raw transport for thin endpoint adapters, with the same retirement
   * boundary as typed methods. A 5xx may be replayed by a lazy chunk so
   * the shell does not carry the retry schedule. */
  readonly fetchApi: FetchLike = (input, init) => {
    if (this.retired) {
      return Promise.reject(new DOMException("Session changed.", "AbortError"));
    }
    return this.doFetch(input, init).then((response) => {
      if (response.status < 500) return response;
      return import("./u.js").then(
        (mod) => mod.go(this as never, input, init, response),
        () => response,
      );
    });
  };

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal; headers?: Record<string, string> },
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...options?.headers,
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (MUTATING.has(method)) {
      const token = this.currentCsrfToken ?? this.csrfToken();
      if (token) headers["X-CSRF-Token"] = token;
    }
    return this.send<T>(
      method,
      path,
      body === undefined ? undefined : JSON.stringify(body),
      headers,
      options,
    );
  }

  /** Shared transport: credentials on every call, one error contract.
   * CORE-389: the fetch runs under the client's timeout ceiling — a
   * stuck call rejects with a `TimeoutError`; a caller-supplied abort
   * still aborts with the caller's own reason. */
  private async send<T>(
    method: string,
    path: string,
    body: BodyInit | undefined,
    headers: Record<string, string>,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    const response = await this.fetchApi(path, {
      method,
      headers,
      credentials: "include",
      body,
      signal: withTimeout(options?.signal, this.timeoutMs),
    });

    if (response.status === 204) return undefined as T;

    let parsed: unknown;
    const text = await response.text();
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = undefined;
      }
    }

    if (!response.ok) {
      const dto = (parsed ?? {}) as Partial<ErrorDto>;
      throw new ApiError(
        dto.code ?? "unavailable",
        response.status,
        dto.request_id,
        dto.blockers,
        dto.detail,
      );
    }
    return parsed as T;
  }

  // -- auth -----------------------------------------------------------------

  register(request: RegisterRequest): Promise<SessionDto> {
    return this.request<SessionDto>("POST", "/auth/register", request);
  }

  login(request: LoginRequest): Promise<SessionDto> {
    return this.request<SessionDto>("POST", "/auth/login", request);
  }

  logout(): Promise<void> {
    return this.request<void>("POST", "/auth/logout", {});
  }

  getSession(options?: { signal?: AbortSignal }): Promise<SessionDto> {
    return this.request<SessionDto>("GET", "/auth/session", undefined, options);
  }

  // -- profile (self-service display name) -------------------------------------

  /** The caller's profile: mentionable display name + linked mailbox. */
  getProfile(options?: { signal?: AbortSignal }): Promise<ProfileDto> {
    return this.request<ProfileDto>(
      "GET",
      "/api/me/profile",
      undefined,
      options,
    );
  }

  /**
   * Sets the caller's display name (self-service; the server accepts only
   * names one @mention token can spell — letters, digits, `.`, `_`, `-`).
   */
  updateProfile(request: UpdateProfileRequest): Promise<ProfileDto> {
    return this.request<ProfileDto>("PATCH", "/api/me/profile", request);
  }

  // -- PKG-J: email verification + password reset -----------------------------
  // (public routes: no session; the uniform ack never discloses accounts)

  /** Confirms the single-use emailed verification token. */
  confirmEmailVerification(
    request: EmailVerificationConfirmRequest,
  ): Promise<EmailVerificationResultDto> {
    return this.request<EmailVerificationResultDto>(
      "POST",
      "/auth/email-verification/confirm",
      request,
    );
  }

  /** Session-scoped re-send of the verification email (202 + rate limit). */
  resendEmailVerification(): Promise<EmailSentAck> {
    return this.request<EmailSentAck>(
      "POST",
      "/auth/email-verification/resend",
      {},
    );
  }

  /** Requests a reset link; the 202 ack is identical for known/unknown addresses. */
  requestPasswordReset(request: PasswordResetRequest): Promise<EmailSentAck> {
    return this.request<EmailSentAck>(
      "POST",
      "/auth/password-reset/request",
      request,
    );
  }

  /** Consumes the reset token, rotates the password and revokes sessions. */
  confirmPasswordReset(
    request: PasswordResetConfirmRequest,
  ): Promise<PasswordChangedAck> {
    return this.request<PasswordChangedAck>(
      "POST",
      "/auth/password-reset/confirm",
      request,
    );
  }

  // -- PKG-J: workspace invitations -------------------------------------------

  /** Token-bearing public view behind the emailed link. */
  getInvitationView(
    token: string,
    options?: { signal?: AbortSignal },
  ): Promise<InvitationViewDto> {
    return this.request<InvitationViewDto>(
      "GET",
      `/auth/invitations/${encodeURIComponent(token)}`,
      undefined,
      options,
    );
  }

  /** Accepts (first call) or replays (idempotent) an invitation. */
  acceptInvitation(token: string): Promise<InvitationAcceptedDto> {
    return this.request<InvitationAcceptedDto>(
      "POST",
      `/auth/invitations/${encodeURIComponent(token)}/accept`,
      {},
    );
  }

  /** Lists a workspace's invitations (manager authority). */
  listInvitations(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<InvitationDto[]> {
    return this.request<InvitationDto[]>(
      "GET",
      `/api/invitations${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  /** Creates (or re-issues) an invitation; the emailed token is server-side. */
  createInvitation(request: CreateInvitationRequest): Promise<InvitationDto> {
    return this.request<InvitationDto>("POST", "/api/invitations", request);
  }

  /** Re-sends the invitation email (rotates the token). */
  resendInvitation(id: string): Promise<InvitationDto> {
    return this.request<InvitationDto>(
      "POST",
      `/api/invitations/${id}/resend`,
      {},
    );
  }

  /** Revokes the invitation link (idempotent). */
  revokeInvitation(id: string): Promise<InvitationDto> {
    return this.request<InvitationDto>(
      "POST",
      `/api/invitations/${id}/revoke`,
      {},
    );
  }

  // -- workspaces ------------------------------------------------------------

  listWorkspaces(options?: { signal?: AbortSignal }): Promise<WorkspaceDto[]> {
    return this.request<WorkspaceDto[]>(
      "GET",
      "/api/workspaces",
      undefined,
      options,
    );
  }

  // -- pages -----------------------------------------------------------------

  listPages(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageSummaryDto[]> {
    return this.request<PageSummaryDto[]>(
      "GET",
      `/api/pages${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  createPage(request: CreatePageRequest): Promise<PageDto> {
    return this.request<PageDto>("POST", "/api/pages", request);
  }

  /**
   * CORE-80B: creates a page from pasted Markdown. The server converts
   * through the shared CORE-32 parser; unsupported constructs arrive as
   * labelled preserved originals. Same create convention and idempotency
   * contract as `createPage`.
   */
  createPageFromMarkdown(
    request: CreatePageFromMarkdownRequest,
  ): Promise<PageDto> {
    return this.request<PageDto>("POST", "/api/pages/from-markdown", request);
  }

  getPage(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageDto> {
    return this.request<PageDto>(
      "GET",
      `/api/pages/${pageId}`,
      undefined,
      options,
    );
  }

  updatePage(pageId: string, request: UpdatePageRequest): Promise<SavedDto> {
    return this.request<SavedDto>("PATCH", `/api/pages/${pageId}`, request);
  }

  // -- collaborative editing (CORE-24) ------------------------------------

  /**
   * Same-epoch CRDT update: a Yjs update (lib0 v1, base64) merged into
   * the page's current epoch. `updateId` is the idempotency key; a
   * rotated epoch refuses stale updates with `epoch_conflict`.
   */
  updatePageCrdt(
    pageId: string,
    request: CrdtUpdateRequest,
  ): Promise<CrdtSavedDto> {
    return this.request<CrdtSavedDto>(
      "POST",
      `/api/pages/${pageId}/crdt`,
      request,
    );
  }

  /**
   * CRDT sync state: current epoch, revision, the server's state vector
   * and the update the caller is missing (`sv` = the caller's base64
   * state vector; omitted → the full epoch state).
   */
  getPageCrdt(
    pageId: string,
    sv?: string,
    options?: { signal?: AbortSignal },
  ): Promise<CrdtStateDto> {
    const suffix = sv ? `?sv=${encodeURIComponent(sv)}` : "";
    return this.request<CrdtStateDto>(
      "GET",
      `/api/pages/${pageId}/crdt${suffix}`,
      undefined,
      options,
    );
  }

  /** Ephemeral presence publish (no content, no authority, never resumed). */
  publishPresence(
    request: PresencePublishRequest,
  ): Promise<{ delivered: number }> {
    return this.request<{ delivered: number }>(
      "POST",
      "/api/realtime/presence",
      request,
    );
  }

  /**
   * CORE-30: register/unregister this session's page watch (the CRDT
   * relay fan-out hint). Read authority is re-checked server-side on
   * every call; the TTL-bounded watch itself grants nothing.
   */
  watchPage(
    pageId: string,
    kind: "watch" | "unwatch" = "watch",
  ): Promise<{ watchers: number }> {
    return this.request<{ watchers: number }>("POST", "/api/realtime/watch", {
      page_id: pageId,
      kind,
    });
  }

  /**
   * Attributed CRDT conflict records for a page (CORE-29), newest
   * first: same-block concurrent edits/moves that lost the merge.
   */
  listPageConflicts(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CrdtConflictDto[]> {
    return this.request<CrdtConflictDto[]>(
      "GET",
      `/api/pages/${pageId}/conflicts`,
      undefined,
      options,
    );
  }

  // -- attachments (pages) ------------------------------------------------------

  /** Lists a page's attachments; also the editor's feature probe. */
  listPageAttachments(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AttachmentDto[]> {
    return this.request<AttachmentDto[]>(
      "GET",
      `/api/pages/${pageId}/attachments`,
      undefined,
      options,
    );
  }

  /**
   * Upload transport: RAW request body (one object per call) with
   * `filename`/`content_type` as query parameters and a fresh idempotency
   * key in `X-Idempotency-Key` — the exact contract the Worker serves
   * (size + SHA-256 are computed server-side from the received bytes).
   */
  createPageAttachment(
    pageId: string,
    file: File,
    options?: { signal?: AbortSignal },
  ): Promise<AttachmentDto> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
    headers["X-Idempotency-Key"] = crypto.randomUUID();
    const params = new URLSearchParams({
      filename: file.name || "attachment",
      content_type: file.type || "application/octet-stream",
    });
    return this.send<AttachmentDto>(
      "POST",
      `/api/pages/${pageId}/attachments?${params.toString()}`,
      file,
      headers,
      options,
    );
  }

  /**
   * Quarantine gate: promotes a just-uploaded attachment to `clean` by
   * re-verifying the STORED object server-side (size + SHA-256). The
   * editor must call this after upload, BEFORE embedding the attachment:
   * server validation rejects blocks/comments referencing non-clean
   * attachments. Idempotent. Requires page Edit (uploader self-service).
   */
  gateAttachment(attachmentId: string): Promise<AttachmentDto> {
    return this.request<AttachmentDto>(
      "POST",
      `/api/attachments/${attachmentId}/gate`,
      {},
    );
  }

  // -- comments (pages) --------------------------------------------------------

  /** CORE-176: edit a comment's body (author-only; ADR-0041). */
  updateComment(
    commentId: string,
    request: UpdateCommentRequest,
  ): Promise<CommentDto> {
    return this.request<CommentDto>(
      "PUT",
      `/api/comments/${commentId}`,
      request,
    );
  }

  listPageComments(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CommentDto[]> {
    return this.request<CommentDto[]>(
      `GET`,
      `/api/pages/${pageId}/comments`,
      undefined,
      options,
    );
  }

  createPageComment(
    pageId: string,
    request: CreateCommentRequest,
  ): Promise<CommentDto> {
    return this.request<CommentDto>(
      "POST",
      `/api/pages/${pageId}/comments`,
      request,
    );
  }

  // -- tasks -------------------------------------------------------------------

  /**
   * Convenience list over the CORE-62 page endpoint (full projection,
   * first page only — the pre-pagination array shape for existing
   * consumers; the server default page size applies). For resumable
   * walks and the compact projection use `listTasksPage`.
   */
  async listTasks(
    params: {
      workspace_id?: string;
      workflow_status?: string;
      /** CORE-81: canonical execution vocabulary; ANDed with workflow_status. */
      execution_status?: ExecutionStatus;
      assignee_id?: string;
      /** CORE-128: comma-separated workspace label keys, any-of. */
      label_keys?: string;
      /** CORE-128: exact priority (urgent|high|medium|low|none). */
      priority?: string;
      /** CORE-128: only tasks scoped to this project (zero rows for foreign-workspace ids). */
      project_id?: string;
      /** CORE-128: only tasks scheduled into this cycle. */
      cycle_id?: string;
      description_page_id?: string;
      /** Reviewer kind of the task's latest review decision (CORE-19 provenance). */
      review_kind?: "human" | "guest" | "agent" | "service";
      /** CORE-4: only tasks whose project is attached to this initiative. */
      initiative_id?: string;
      limit?: number;
    },
    options?: { signal?: AbortSignal },
  ): Promise<TaskDto[]> {
    const page = await this.request<TaskListPage>(
      "GET",
      `/api/tasks${qs(params)}`,
      undefined,
      options,
    );
    return page.items;
  }

  /**
   * CORE-62: one bounded, cursor-resumable task list page. Ordering is
   * `sort` + `direction` (default `updated_at DESC, id DESC`; CORE-128);
   * `next_cursor` is absent on the last page — pass it back as `cursor`
   * to resume. A cursor is a POSITION, not a snapshot (rows updated
   * mid-walk move to the list head; re-list to converge) and carries its
   * own ordering: a cursor presented under a different sort is an
   * actionable 400. `projection: "compact"` opts into the bounded field
   * set. Unknown `workflow_status` / `execution_status` / `priority` /
   * `sort` / `direction` / `projection` values throw ApiError with
   * `detail` naming the valid values. CORE-128 filters (`label_keys` any-
   * of, `priority`, `project_id`, `cycle_id`) run under the SAME
   * workspace admission predicate — foreign-workspace ids match zero
   * rows, never a leak. CORE-81: `execution_status` filters the
   * projected execution state — a lapsed lease projects `unclaimed` on
   * read; only a live lease reads `claimed` (CORE-84).
   */
  listTasksPage(
    params: {
      workspace_id?: string;
      workflow_status?: string;
      /** CORE-81: canonical execution vocabulary; ANDed with workflow_status. */
      execution_status?: ExecutionStatus;
      assignee_id?: string;
      /** CORE-128: comma-separated workspace label keys, any-of. */
      label_keys?: string;
      /** CORE-128: exact priority (urgent|high|medium|low|none). */
      priority?: string;
      /** CORE-128: only tasks scoped to this project. */
      project_id?: string;
      /** CORE-128: only tasks scheduled into this cycle. */
      cycle_id?: string;
      /** CORE-164: hide done tasks last updated before now minus this
       * window (1d|7d|30d) — a server-side page constraint, so filtered
       * rows never break cursor continuation. */
      hide_done_older_than?: "1d" | "7d" | "30d";
      description_page_id?: string;
      review_kind?: "human" | "guest" | "agent" | "service";
      initiative_id?: string;
      /** CORE-128: ordering field (updated_at|created_at|title|priority). */
      sort?: "updated_at" | "created_at" | "title" | "priority";
      /** CORE-128: ordering direction. */
      direction?: "asc" | "desc";
      /** Page size, clamped server-side into 1..=200 (default 50). */
      limit?: number;
      /** Opaque continuation cursor from the previous page. */
      cursor?: string;
      /** Opt-in bounded projection. */
      projection?: "compact";
    },
    options?: { signal?: AbortSignal },
  ): Promise<TaskListPage | TaskCompactListPage> {
    return this.request<TaskListPage | TaskCompactListPage>(
      "GET",
      `/api/tasks${qs(params)}`,
      undefined,
      options,
    );
  }

  createTask(request: CreateTaskRequest): Promise<TaskDto> {
    return this.request<TaskDto>("POST", "/api/tasks", request);
  }

  /**
   * CORE-248/CORE-249 (ADR-0048): one-tap AI compose. The work brief is
   * the input; the server plans AND files in the same operation under
   * the caller's own task-creation authority — a single task, or (when
   * the typed plan calls for it, or the caller forces `shape`) a linked
   * brief page plus its task set. The receipt names the filed shape,
   * the plan's reasoning and every filed object; `task` is the primary
   * task to open in edit. One metered `edge = compose` spend-ledger row
   * per call. Bounded failures (empty/oversized brief, injection
   * attempt, policy off, rate limit, provider failure) throw ApiError
   * with a named `detail` reason and file NOTHING.
   */
  composeTask(request: ComposeTaskRequest): Promise<ComposeResultDto> {
    return this.request<ComposeResultDto>(
      "POST",
      "/api/tasks/compose",
      request,
    );
  }

  /**
   * CORE-248/CORE-249: real undo of a just-composed filing — a bounded
   * retraction of the WHOLE set (not a UI dismissal). Only the
   * composer, within the 600-second window, while nothing has
   * attached. The receipt lists exactly what was removed.
   */
  undoComposeTask(
    taskId: string,
    request: UndoComposeRequest,
  ): Promise<UndoComposeReceiptDto> {
    return this.request<UndoComposeReceiptDto>(
      "POST",
      `/api/tasks/${taskId}/compose-undo`,
      request,
    );
  }

  /** CORE-248: the workspace compose policy (absent row = enabled). */
  getWorkspaceComposeSetting(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WorkspaceComposeSettingDto> {
    return this.request<WorkspaceComposeSettingDto>(
      "GET",
      `/api/workspaces/${workspaceId}/compose-setting`,
      undefined,
      options,
    );
  }
  /**
   * CORE-246: transcribe one recorded clip for the compose modal.
   * The audio rides the attachments path's raw-body shape (the blob's
   * own media type in Content-Type, the clip length in the query); the
   * returned transcript fills the modal's editable textarea and files
   * NOTHING — the compose tap stays a separate, user-initiated action.
   * One metered `edge = transcribe` spend-ledger row per call; the
   * same per-workspace policy gate as compose throws `compose_disabled`.
   * Audio is transcribe-and-discard: it is never persisted anywhere.
   */
  async transcribeComposeAudio(
    workspaceId: string,
    audio: Blob,
    durationSeconds: number,
    idempotencyKey: string,
    options?: { signal?: AbortSignal },
  ): Promise<TranscribeResultDto> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      // The blob carries its recorded media type (audio/webm, audio/mp4).
      "Content-Type": audio.type || "audio/webm",
      "Idempotency-Key": idempotencyKey,
    };
    const token = this.currentCsrfToken ?? this.csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
    return this.send<TranscribeResultDto>(
      "POST",
      `/api/workspaces/${workspaceId}/compose/transcribe?duration_seconds=${Math.max(
        0,
        Math.round(durationSeconds),
      )}`,
      audio,
      headers,
      options,
    );
  }

  /** CORE-248: replace the workspace compose policy (Manager+). */
  setWorkspaceComposeSetting(
    workspaceId: string,
    request: SetWorkspaceComposeSettingRequest,
  ): Promise<WorkspaceComposeSettingDto> {
    return this.request<WorkspaceComposeSettingDto>(
      "POST",
      `/api/workspaces/${workspaceId}/compose-setting`,
      request,
    );
  }

  getTask(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "GET",
      `/api/tasks/${taskId}`,
      undefined,
      options,
    );
  }

  /** Blocking dependency edges pointing AT the task (CORE-25). */
  listTaskDependencies(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDependencyDto[]> {
    return this.request<TaskDependencyDto[]>(
      "GET",
      `/api/tasks/${taskId}/dependencies`,
      undefined,
      options,
    );
  }

  /** Add a blocking dependency edge (cycle/self-edge/cross-workspace refused server-side). */
  addTaskDependency(
    taskId: string,
    request: AddTaskDependencyRequest,
  ): Promise<TaskDependencyDto> {
    return this.request<TaskDependencyDto>(
      "POST",
      `/api/tasks/${taskId}/dependencies`,
      request,
    );
  }

  /** Remove one blocking dependency edge; idempotent. Optional scoped idempotency key. */
  removeTaskDependency(
    taskId: string,
    dependsOnTaskId: string,
    idempotencyKey?: string,
  ): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/tasks/${taskId}/dependencies/${dependsOnTaskId}`,
      undefined,
      {
        headers: idempotencyKey
          ? { "X-Idempotency-Key": idempotencyKey }
          : undefined,
      },
    );
  }

  listTaskComments(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CommentDto[]> {
    return this.request<CommentDto[]>(
      "GET",
      `/api/tasks/${taskId}/comments`,
      undefined,
      options,
    );
  }

  createTaskComment(
    taskId: string,
    request: CreateCommentRequest,
  ): Promise<CommentDto> {
    return this.request<CommentDto>(
      "POST",
      `/api/tasks/${taskId}/comments`,
      request,
    );
  }

  /**
   * Bounded task activity (task_events, newest first, seq cursor).
   * Grouping of consecutive same-actor state changes is client-side.
   */
  listTaskActivity(
    taskId: string,
    params: { limit?: number; cursor?: string } = {},
    options?: { signal?: AbortSignal },
  ): Promise<ActivityDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<ActivityDto>(
      "GET",
      `/api/tasks/${taskId}/activity${search}`,
      undefined,
      options,
    );
  }

  /**
   * The task's machine-filing triage record (CORE-104): dedupe key,
   * classification, verdicts, route intents and — when the filing
   * automation declared a decide recipe — the CORE-117 decision
   * attachment (probabilities + provenance + spend as data).
   * 404 when the task is not a machine-filed proposal.
   */
  getTaskTriage(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskTriageProposal> {
    return this.request<TaskTriageProposal>(
      "GET",
      `/api/tasks/${taskId}/triage`,
      undefined,
      options,
    );
  }
  /**
   * The ORDERED reviewer queue (CORE-121): awaiting-review completions
   * prioritized by the review-priority decide scores. The order IS the
   * prioritization surface — the scores never gate acceptance.
   */
  listReviewQueue(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ReviewQueue> {
    return this.request<ReviewQueue>(
      "GET",
      `/api/workspaces/${workspaceId}/review-queue`,
      undefined,
      options,
    );
  }
  /**
   * The task's CORE-121 decide annotations: the review-priority
   * ordering hint and the risk-class suggestion (when annotated).
   */
  getTaskDecideEnrichments(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDecideEnrichments> {
    return this.request<TaskDecideEnrichments>(
      "GET",
      `/api/tasks/${taskId}/decide-enrichments`,
      undefined,
      options,
    );
  }
  /**
   * One owner's CORE-122 goal decide verdicts: the quality attachment
   * (well-formedness dimensions + coherence) and the post-release
   * drift attachment. Verdicts are data on the goal — never a gate.
   */
  getGoalDecideEnrichments(
    ownerKind: "project" | "initiative",
    ownerId: string,
    options?: { signal?: AbortSignal },
  ): Promise<GoalDecideEnrichments> {
    return this.request<GoalDecideEnrichments>(
      "GET",
      `/api/goals/${ownerKind}/${ownerId}/decide-enrichments`,
      undefined,
      options,
    );
  }
  /**
   * The human/policy CONFIRM path of the risk-class suggestion
   * (CORE-121): sets the task's deterministic class (manager-gated,
   * idempotent; refused on done/canceled tasks).
   */
  setTaskRiskClass(
    taskId: string,
    body: SetTaskRiskClassRequest,
  ): Promise<TaskRiskClass> {
    return this.request<TaskRiskClass>(
      "POST",
      `/api/tasks/${taskId}/risk-class`,
      body,
    );
  }

  /**
   * CORE-193: the task's mid-attempt approval checkpoints, newest
   * first, with the caller's decide-authority projection. A pending
   * request parks the task; read authority on the task.
   */
  listTaskApprovals(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskApprovalList> {
    return this.request<TaskApprovalList>(
      "GET",
      `/api/tasks/${taskId}/approvals`,
      undefined,
      options,
    );
  }

  /**
   * CORE-193: grant or deny a pending approval checkpoint. Decide
   * authority (designated approver per risk class, managers as the
   * unconfigured fallback) is re-checked server-side; a stale answer
   * refuses with `approval_stale` and never rewrites the recorded
   * outcome.
   */
  decideTaskApproval(
    taskId: string,
    requestId: string,
    body: DecideTaskApprovalRequest,
  ): Promise<TaskApprovalRequest> {
    return this.request<TaskApprovalRequest>(
      "POST",
      `/api/tasks/${taskId}/approvals/${requestId}/decide`,
      body,
    );
  }

  /**
   * CORE-120: run one bounded dependency-inference pass (Manager). The
   * pass is idempotent (per-pair state-hash skip keys); a judged
   * `blocks` becomes a PROPOSED edge awaiting ordinary acceptance.
   */
  runDependencyInference(
    workspaceId: string,
  ): Promise<DependencyInferenceSummary> {
    return this.request<DependencyInferenceSummary>(
      "POST",
      `/api/workspaces/${workspaceId}/dependency-inference`,
      undefined,
    );
  }

  /**
   * CORE-120: list the workspace's dependency-inference proposals
   * (optional closed-vocabulary status filter).
   */
  listDependencyProposals(
    workspaceId: string,
    params: { status?: DependencyProposalStatus } = {},
    options?: { signal?: AbortSignal },
  ): Promise<DependencyProposalList> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<DependencyProposalList>(
      "GET",
      `/api/workspaces/${workspaceId}/dependency-proposals${search}`,
      undefined,
      options,
    );
  }

  /**
   * CORE-118: run one bounded dedupe-judge pass (Manager). Loose
   * token-Jaccard nominates candidate pairs; each is judged through
   * ONE decide call over the closed dedupe_relationship vocabulary;
   * only `duplicate` at/above the workspace POLICY threshold triggers
   * the EXISTING link-don't-refile path. Idempotent by per-pair skip
   * keys, so the route takes no idempotency envelope.
   */
  runDedupeJudge(workspaceId: string): Promise<DedupeJudgeSummary> {
    return this.request<DedupeJudgeSummary>(
      "POST",
      `/api/workspaces/${workspaceId}/dedupe-judge`,
      undefined,
    );
  }
  /**
   * CORE-118: list the workspace's judged dedupe pairs (optional
   * closed-vocabulary status filter; the status names the ACTION).
   */
  listDedupeJudgements(
    workspaceId: string,
    params: { status?: DedupeJudgementStatus } = {},
    options?: { signal?: AbortSignal },
  ): Promise<DedupeJudgementList> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<DedupeJudgementList>(
      "GET",
      `/api/workspaces/${workspaceId}/dedupe-judgements${search}`,
      undefined,
      options,
    );
  }
  /**
   * CORE-118: read the workspace dedupe policy (members).
   */
  getWorkspaceDedupePolicy(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WorkspaceDedupePolicy> {
    return this.request<WorkspaceDedupePolicy>(
      "GET",
      `/api/workspaces/${workspaceId}/dedupe-policy`,
      undefined,
      options,
    );
  }
  /**
   * CORE-118: set the workspace dedupe policy (Manager; whole-row
   * replace of the two thresholds; idempotent by value).
   */
  setWorkspaceDedupePolicy(
    workspaceId: string,
    body: {
      duplicate_threshold: number;
      candidate_threshold?: number;
      idempotency_key: string;
    },
  ): Promise<WorkspaceDedupePolicy> {
    return this.request<WorkspaceDedupePolicy>(
      "PUT",
      `/api/workspaces/${workspaceId}/dedupe-policy`,
      { workspace_id: workspaceId, ...body },
    );
  }
  /**
   * CORE-120/CORE-287: accept a proposed task proposal — propose ≠
   * execute. A `dependency` kind creates the edge through the ORDINARY
   * add-dependency path; a `duplicate` kind links by comment on the
   * existing item and cancels the duplicate; a `related` kind posts
   * the comment link. The receipt carries the executed effect.
   */
  acceptDependencyProposal(
    proposalId: string,
    idempotencyKey: string,
  ): Promise<AcceptedProposal> {
    return this.request<AcceptedProposal>(
      "POST",
      `/api/dependency-proposals/${proposalId}/accept`,
      { idempotency_key: idempotencyKey },
    );
  }

  /**
   * CORE-120: reject a proposed edge — provenance retained, no edge.
   */
  rejectDependencyProposal(
    proposalId: string,
    idempotencyKey: string,
    note = "",
  ): Promise<DependencyProposal> {
    return this.request<DependencyProposal>(
      "POST",
      `/api/dependency-proposals/${proposalId}/reject`,
      { note, idempotency_key: idempotencyKey },
    );
  }

  /**
   * CORE-287: the pick-up-time read — the task's similar OPEN tasks
   * with any open proposal between them. Zero model calls.
   */
  getTaskRelated(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RelatedTasks> {
    return this.request<RelatedTasks>(
      "GET",
      `/api/tasks/${taskId}/related`,
      undefined,
      options,
    );
  }

  /**
   * CORE-287: a claiming agent files a duplicate/related proposal for
   * a human to accept (its finding is the nomination; acceptance
   * keeps its ordinary gate).
   */
  fileTaskProposal(
    taskId: string,
    body: {
      related_task_id: string;
      kind: "duplicate" | "related";
      note?: string;
      idempotency_key: string;
    },
  ): Promise<DependencyProposal> {
    return this.request<DependencyProposal>(
      "POST",
      `/api/tasks/${taskId}/proposals`,
      body,
    );
  }

  /**
   * CORE-120: the typed blockers surface — zero-runnable chains, named
   * cycles, dead ends, stale claims and review-latency outliers.
   * Read-only data over canonical state.
   */
  getWorkspaceBlockers(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WorkspaceBlockers> {
    return this.request<WorkspaceBlockers>(
      "GET",
      `/api/workspaces/${workspaceId}/blockers`,
      undefined,
      options,
    );
  }

  /**
   * Bounded page activity (structural audit projection, newest first,
   * opaque keyset cursor): created + renamed entries.
   */
  listPageActivity(
    pageId: string,
    params: { limit?: number; cursor?: string } = {},
    options?: { signal?: AbortSignal },
  ): Promise<ActivityDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<ActivityDto>(
      "GET",
      `/api/pages/${pageId}/activity${search}`,
      undefined,
      options,
    );
  }

  /**
   * Merged workspace activity feed (PKG-R): task + page activity sources
   * in one newest-first page with an opaque keyset cursor. Requires
   * workspace admission; restricted-page ACLs are enforced server-side.
   */
  activityFeed(
    params: { workspace_id: string; limit?: number; cursor?: string },
    options?: { signal?: AbortSignal },
  ): Promise<ActivityFeedDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<ActivityFeedDto>(
      "GET",
      `/api/activity${search}`,
      undefined,
      options,
    );
  }

  // -- per-principal inbox + watches (PKG-X — M2C-02) ---------------------

  /**
   * The caller's DERIVED inbox (GET /api/inbox): events on watched
   * resources plus directed assignments/review requests, newest first,
   * with the caller's acked position and unread flag. Agents pass
   * context_session_id for their session-scoped position.
   */
  inbox(
    params: {
      workspace_id: string;
      limit?: number;
      cursor?: string;
      context_session_id?: string;
    },
    options?: { signal?: AbortSignal },
  ): Promise<InboxDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<InboxDto>(
      "GET",
      `/api/inbox${search}`,
      undefined,
      options,
    );
  }

  /**
   * Advance the caller's inbox read position (POST /api/inbox/ack).
   * Absent cursor = mark everything currently visible read. Idempotent.
   */
  ackInbox(request: AckInboxRequest): Promise<InboxAckDto> {
    return this.request<InboxAckDto>("POST", "/api/inbox/ack", request);
  }

  /** Set or clear one watch (POST /api/watch). Idempotent subscription. */
  setWatch(request: SetWatchRequest): Promise<WatchDto> {
    return this.request<WatchDto>("POST", "/api/watch", request);
  }

  /** The caller's watch set in one workspace (GET /api/watches). */
  listWatches(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WatchListDto> {
    return this.request<WatchListDto>(
      "GET",
      `/api/watches?workspace_id=${encodeURIComponent(workspaceId)}`,
      undefined,
      options,
    );
  }

  claim(taskId: string, request: ClaimRequest): Promise<LeaseDto> {
    return this.request<LeaseDto>(
      "POST",
      `/api/tasks/${taskId}/claim`,
      request,
    );
  }

  heartbeat(
    taskId: string,
    request: { attempt_id: string; fence: number; lease_seconds?: number },
  ): Promise<LeaseDto> {
    return this.request<LeaseDto>(
      "POST",
      `/api/tasks/${taskId}/heartbeat`,
      request,
    );
  }

  /**
   * Drift D2 fix: the route returns `{task, completion_id}` — the completion_id
   * is what review binds to, so callers must keep it.
   */
  complete(
    taskId: string,
    request: CompleteRequest,
  ): Promise<TaskCompletionDto> {
    return this.request<TaskCompletionDto>(
      "POST",
      `/api/tasks/${taskId}/complete`,
      request,
    );
  }

  review(taskId: string, request: ReviewRequest): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/review`,
      request,
    );
  }

  /** CORE-94: ask a specific principal to review this task. */
  requestTaskReview(
    taskId: string,
    request: ReviewRequestNotificationRequest,
  ): Promise<ReviewRequestReceiptDto> {
    return this.request<ReviewRequestReceiptDto>(
      "POST",
      `/api/tasks/${taskId}/review-request`,
      request,
    );
  }

  transition(taskId: string, request: TransitionRequest): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/transition`,
      request,
    );
  }

  /**
   * CORE-61: replace a task's description (docs/19 `task_update`
   * description-first slice). The COMPLETE new text ("" clears); carries
   * the exact task-row activity revision (stale = 409 revision_conflict).
   */
  updateTask(taskId: string, request: UpdateTaskRequest): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/update`,
      request,
    );
  }

  /**
   * CORE-336: set a task's priority (docs/19 task_update priority
   * slice). Closed vocabulary urgent|high|medium|low|none (none
   * clears); carries the exact task-row activity revision (stale =
   * 409 revision_conflict).
   */
  setTaskPriority(
    taskId: string,
    request: SetTaskPriorityRequest,
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/priority`,
      request,
    );
  }

  /**
   * Set or clear a task's assignee. `assignee_id: null` clears. Manage
   * authority is enforced server-side. Does not claim.
   */
  /** The workspace's projects (board grouping labels; PKG-M routes). */
  listProjects(
    workspaceId: string,
    params: { include_archived?: boolean; initiative_id?: string } = {},
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDto[]> {
    return this.request<ProjectDto[]>(
      "GET",
      `/api/projects${qs({ workspace_id: workspaceId, include_archived: params.include_archived ? "true" : undefined, initiative_id: params.initiative_id })}`,
      undefined,
      options,
    );
  }

  /**
   * Renames a project and/or updates its task key prefix (CORE-10;
   * Manage authority is enforced server-side). Absent `key_prefix`
   * leaves the prefix unchanged; either way only FUTURE tasks change —
   * existing issue keys never renumber.
   */
  updateProject(
    projectId: string,
    request: UpdateProjectRequest,
  ): Promise<ProjectDto> {
    return this.request<ProjectDto>(
      "PATCH",
      `/api/projects/${projectId}`,
      request,
    );
  }

  // -- initiatives (CORE-4 — ordering of projects; grants nothing) --------

  /** The workspace's initiatives, each with its live rollup. */
  listInitiatives(
    workspaceId: string,
    params: { include_archived?: boolean; limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<InitiativeSummaryDto[]> {
    return this.request<InitiativeSummaryDto[]>(
      "GET",
      `/api/initiatives${qs({ workspace_id: workspaceId, include_archived: params.include_archived ? "true" : undefined, limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** One initiative: ordered member projects + rollup. */
  getInitiative(
    initiativeId: string,
    options?: { signal?: AbortSignal },
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "GET",
      `/api/initiatives/${initiativeId}`,
      undefined,
      options,
    );
  }

  /** Creates an initiative (workspace manager/admin only). */
  createInitiative(request: CreateInitiativeRequest): Promise<InitiativeDto> {
    return this.request<InitiativeDto>("POST", "/api/initiatives", request);
  }

  /** Renames an initiative (exact expected_revision precondition). */
  updateInitiative(
    initiativeId: string,
    request: UpdateInitiativeRequest,
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "PATCH",
      `/api/initiatives/${initiativeId}`,
      request,
    );
  }

  /** Archives an initiative (never touches member projects or tasks). */
  archiveInitiative(
    initiativeId: string,
    request: ArchiveInitiativeRequest,
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "POST",
      `/api/initiatives/${initiativeId}/archive`,
      request,
    );
  }

  /** Attaches an active project (fractional position; absent anchor = top). */
  attachInitiativeProject(
    initiativeId: string,
    request: AttachInitiativeProjectRequest,
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "POST",
      `/api/initiatives/${initiativeId}/projects`,
      request,
    );
  }

  /** Detaches a project (tasks are never touched). */
  detachInitiativeProject(
    initiativeId: string,
    request: DetachInitiativeProjectRequest,
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "POST",
      `/api/initiatives/${initiativeId}/projects/detach`,
      request,
    );
  }

  /** Moves an attached project (one fractional-position write; absent anchor = top). */
  reorderInitiativeProject(
    initiativeId: string,
    request: ReorderInitiativeProjectRequest,
  ): Promise<InitiativeDto> {
    return this.request<InitiativeDto>(
      "POST",
      `/api/initiatives/${initiativeId}/projects/reorder`,
      request,
    );
  }

  // -- goals (CORE-101 — platform goal state with typed metric refs) -----

  /** A goal with current metric values + the computed freshness verdict. */
  getGoal(
    ownerKind: GoalOwnerKind,
    ownerId: string,
    options?: { signal?: AbortSignal },
  ): Promise<GoalDto> {
    return this.request<GoalDto>(
      "GET",
      `/api/goals/${ownerKind}/${ownerId}`,
      undefined,
      options,
    );
  }

  /** Creates the owner's goal (workspace manager/admin; one per owner). */
  createGoal(request: CreateGoalRequest): Promise<GoalDto> {
    return this.request<GoalDto>("POST", "/api/goals", request);
  }

  /** Updates a goal (absent fields keep values; exact revision CAS). */
  updateGoal(
    ownerKind: GoalOwnerKind,
    ownerId: string,
    request: UpdateGoalRequest,
  ): Promise<GoalDto> {
    return this.request<GoalDto>(
      "PATCH",
      `/api/goals/${ownerKind}/${ownerId}`,
      request,
    );
  }

  /** Deletes a goal (exact revision CAS; echoes the removed final state). */
  deleteGoal(
    ownerKind: GoalOwnerKind,
    ownerId: string,
    request: DeleteGoalRequest,
  ): Promise<GoalDto> {
    return this.request<GoalDto>(
      "POST",
      `/api/goals/${ownerKind}/${ownerId}/delete`,
      request,
    );
  }

  // -- content templates (CORE-78 — snapshots; a template grants nothing) --

  /** The workspace's page-template gallery (structural visibility). */
  listPageTemplates(
    workspaceId: string,
    params: { include_archived?: boolean; limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<PageTemplateDto[]> {
    return this.request<PageTemplateDto[]>(
      "GET",
      `/api/page-templates${qs({ workspace_id: workspaceId, include_archived: params.include_archived ? "true" : undefined, limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** One page template with its block snapshot. */
  getPageTemplate(
    templateId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageTemplateDto> {
    return this.request<PageTemplateDto>(
      "GET",
      `/api/page-templates/${templateId}`,
      undefined,
      options,
    );
  }

  /** Creates a page template from authored blocks (editor+). */
  createPageTemplate(
    request: CreatePageTemplateRequest,
  ): Promise<PageTemplateDto> {
    return this.request<PageTemplateDto>(
      "POST",
      "/api/page-templates",
      request,
    );
  }

  /** Saves an existing page as a template (data snapshot; page edit authority). */
  createPageTemplateFromPage(
    request: CreatePageTemplateFromPageRequest,
  ): Promise<PageTemplateDto> {
    return this.request<PageTemplateDto>(
      "POST",
      "/api/page-templates/from-page",
      request,
    );
  }

  /** Authors a page template (manager/admin; exact revision). */
  updatePageTemplate(
    templateId: string,
    request: UpdatePageTemplateRequest,
  ): Promise<PageTemplateDto> {
    return this.request<PageTemplateDto>(
      "PATCH",
      `/api/page-templates/${templateId}`,
      request,
    );
  }

  /** Archives a page template (hidden from the default gallery). */
  archivePageTemplate(
    templateId: string,
    request: ArchivePageTemplateRequest,
  ): Promise<PageTemplateDto> {
    return this.request<PageTemplateDto>(
      "POST",
      `/api/page-templates/${templateId}/archive`,
      request,
    );
  }

  /** Deletes a page template (pages created from it are untouched). */
  deletePageTemplate(templateId: string): Promise<void> {
    return this.request<void>("DELETE", `/api/page-templates/${templateId}`);
  }

  /** Applies a page template — stamps a fresh page (same admission as a direct create). */
  applyPageTemplate(
    templateId: string,
    request: ApplyPageTemplateRequest,
  ): Promise<PageDto> {
    return this.request<PageDto>(
      "POST",
      `/api/page-templates/${templateId}/apply`,
      request,
    );
  }

  /** Lists a data source's row templates (source anchor admission). */
  listRowTemplates(
    dataSourceId: string,
    params: { include_archived?: boolean; limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<RowTemplateDto[]> {
    return this.request<RowTemplateDto[]>(
      "GET",
      `/api/data-sources/${dataSourceId}/row-templates${qs({ include_archived: params.include_archived ? "true" : undefined, limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** One row template (defaults + repeat rule). */
  getRowTemplate(
    templateId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RowTemplateDto> {
    return this.request<RowTemplateDto>(
      "GET",
      `/api/row-templates/${templateId}`,
      undefined,
      options,
    );
  }

  /** Creates a row template on a typed data source (editor+). */
  createRowTemplate(
    dataSourceId: string,
    request: CreateRowTemplateRequest,
  ): Promise<RowTemplateDto> {
    return this.request<RowTemplateDto>(
      "POST",
      `/api/data-sources/${dataSourceId}/row-templates`,
      request,
    );
  }

  /** Authors a row template (manager/admin; exact revision). */
  updateRowTemplate(
    templateId: string,
    request: UpdateRowTemplateRequest,
  ): Promise<RowTemplateDto> {
    return this.request<RowTemplateDto>(
      "PATCH",
      `/api/row-templates/${templateId}`,
      request,
    );
  }

  /** Archives a row template (stops further repeat spawns). */
  archiveRowTemplate(
    templateId: string,
    request: ArchiveRowTemplateRequest,
  ): Promise<RowTemplateDto> {
    return this.request<RowTemplateDto>(
      "POST",
      `/api/row-templates/${templateId}/archive`,
      request,
    );
  }

  /** Deletes a row template (rows and spawn chains are untouched). */
  deleteRowTemplate(templateId: string): Promise<void> {
    return this.request<void>("DELETE", `/api/row-templates/${templateId}`);
  }

  /** New row from template — stamps the defaults onto a fresh row. */
  applyRowTemplate(
    templateId: string,
    request: ApplyRowTemplateRequest,
  ): Promise<DatabaseRowDto> {
    return this.request<DatabaseRowDto>(
      "POST",
      `/api/row-templates/${templateId}/apply`,
      request,
    );
  }

  // -- labels, cycles, saved views (PKG-Q — M2 task domain) ---------------

  /** The workspace's triage vocabulary (board filters + pickers). */
  listLabels(
    workspaceId: string,
    params: { include_archived?: boolean } = {},
    options?: { signal?: AbortSignal },
  ): Promise<LabelDto[]> {
    return this.request<LabelDto[]>(
      "GET",
      `/api/labels${qs({ workspace_id: workspaceId, include_archived: params.include_archived ? "true" : undefined })}`,
      undefined,
      options,
    );
  }

  createLabel(request: CreateLabelRequest): Promise<LabelDto> {
    return this.request<LabelDto>("POST", "/api/labels", request);
  }

  updateLabel(labelId: string, request: UpdateLabelRequest): Promise<LabelDto> {
    return this.request<LabelDto>("PATCH", `/api/labels/${labelId}`, request);
  }

  /** Soft archive: task links keep history; new associations are refused. */
  archiveLabel(labelId: string): Promise<LabelDto> {
    return this.request<LabelDto>("POST", `/api/labels/${labelId}/archive`, {
      idempotency_key: crypto.randomUUID(),
    });
  }

  /** Replaces the task's COMPLETE label set (empty clears). */
  setTaskLabels(
    taskId: string,
    request: SetTaskLabelsRequest,
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/labels`,
      request,
    );
  }

  /** The workspace's cycles (active+upcoming by default). */
  listCycles(
    workspaceId: string,
    params: { include_completed?: boolean } = {},
    options?: { signal?: AbortSignal },
  ): Promise<CycleDto[]> {
    return this.request<CycleDto[]>(
      "GET",
      `/api/cycles${qs({ workspace_id: workspaceId, include_completed: params.include_completed ? "true" : undefined })}`,
      undefined,
      options,
    );
  }

  createCycle(request: CreateCycleRequest): Promise<CycleDto> {
    return this.request<CycleDto>("POST", "/api/cycles", request);
  }

  /** Explicit lifecycle command; never touches member tasks. */
  setCycleStatus(
    cycleId: string,
    request: SetCycleStatusRequest,
  ): Promise<CycleDto> {
    return this.request<CycleDto>(
      "POST",
      `/api/cycles/${cycleId}/status`,
      request,
    );
  }

  /** Task command (never a raw field overwrite); absent clears. */
  setTaskCycle(taskId: string, request: SetTaskCycleRequest): Promise<TaskDto> {
    return this.request<TaskDto>("POST", `/api/tasks/${taskId}/cycle`, request);
  }
  /** Attach/clear a task's linked data source (CORE-11; link grants no access). */
  setTaskDataSource(
    taskId: string,
    request: SetTaskDataSourceRequest,
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/data-source`,
      request,
    );
  }

  /** Saved board views (persisted query specs; applying is client-side). */
  listBoardViews(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<BoardViewDto[]> {
    return this.request<BoardViewDto[]>(
      "GET",
      `/api/views${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  createBoardView(request: CreateBoardViewRequest): Promise<BoardViewDto> {
    return this.request<BoardViewDto>("POST", "/api/views", request);
  }

  /** Creator-or-Manage server-side; 204 on success. */
  deleteBoardView(viewId: string): Promise<void> {
    return this.request<void>("DELETE", `/api/views/${viewId}`);
  }

  // -- work types, typed fields, substates (PKG-U — M2C-03) ----------

  /** The workspace's work types (issue shapes; membership read). */
  listWorkTypes(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WorkTypeDto[]> {
    return this.request<WorkTypeDto[]>(
      "GET",
      `/api/work-types${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  /** Creates a work type (Manage on the workspace). */
  createWorkType(request: CreateWorkTypeRequest): Promise<WorkTypeDto> {
    return this.request<WorkTypeDto>("POST", "/api/work-types", request);
  }

  /** Updates a work type (Manage; schema changes bump the revision). */
  updateWorkType(
    workTypeId: string,
    request: UpdateWorkTypeRequest,
  ): Promise<WorkTypeDto> {
    return this.request<WorkTypeDto>(
      "PATCH",
      `/api/work-types/${workTypeId}`,
      request,
    );
  }

  /** Immutable schema revision history. */
  listWorkTypeRevisions(
    workTypeId: string,
    options?: { signal?: AbortSignal },
  ): Promise<WorkTypeRevisionDto[]> {
    return this.request<WorkTypeRevisionDto[]>(
      "GET",
      `/api/work-types/${workTypeId}/revisions`,
      undefined,
      options,
    );
  }

  // -- risk-tiered review policy (CORE-105) ---------------------------

  /** The workspace's class→behavior review policy (membership read). */
  getReviewPolicy(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ReviewPolicyDto> {
    return this.request<ReviewPolicyDto>(
      "GET",
      `/api/workspaces/${workspaceId}/review-policy`,
      undefined,
      options,
    );
  }

  /** Whole-mapping policy replace (manager+; idempotency-keyed). */
  setReviewPolicy(
    workspaceId: string,
    request: SetReviewPolicyRequest,
  ): Promise<SetReviewPolicyReceiptDto> {
    return this.request<SetReviewPolicyReceiptDto>(
      "PUT",
      `/api/workspaces/${workspaceId}/review-policy`,
      request,
    );
  }

  /** Metered automation usage grouped by run identity (cost attribution). */
  listUsageAttribution(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<UsageAttributionRowDto[]> {
    return this.request<UsageAttributionRowDto[]>(
      "GET",
      `/api/workspaces/${workspaceId}/usage-attribution`,
      undefined,
      options,
    );
  }

  /** Task command: adopt/clear a work type + typed values (validated server-side). */
  setTaskWorkType(
    taskId: string,
    request: SetTaskWorkTypeRequest,
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/tasks/${taskId}/work-type`,
      request,
    );
  }

  // -- milestones, templates, bulk (PKG-T — M2C-01) -------------------------

  /** A cycle's milestones (nested checkpoints; active+upcoming default). */
  listMilestones(
    cycleId: string,
    params: { include_completed?: boolean } = {},
    options?: { signal?: AbortSignal },
  ): Promise<MilestoneDto[]> {
    return this.request<MilestoneDto[]>(
      "GET",
      `/api/cycles/${cycleId}/milestones${qs({ include_completed: params.include_completed ? "true" : undefined })}`,
      undefined,
      options,
    );
  }

  /** Cycle close: completed + per-item carryover report (docs19). */
  closeCycle(cycleId: string): Promise<CloseCycleResponseDto> {
    return this.request<CloseCycleResponseDto>(
      "POST",
      `/api/cycles/${cycleId}/close`,
      { idempotency_key: crypto.randomUUID() },
    );
  }

  /** Pre-close dry run or post-close history of the conflict report. */
  carryoverReport(
    cycleId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CarryoverReportDto> {
    return this.request<CarryoverReportDto>(
      "GET",
      `/api/cycles/${cycleId}/carryover-report`,
      undefined,
      options,
    );
  }

  /** Template families at their LATEST version (one entry per family). */
  listTemplates(
    workspaceId: string,
    params: { include_archived?: boolean } = {},
    options?: { signal?: AbortSignal },
  ): Promise<TemplateDto[]> {
    return this.request<TemplateDto[]>(
      "GET",
      `/api/templates${qs({ workspace_id: workspaceId, include_archived: params.include_archived ? "true" : undefined })}`,
      undefined,
      options,
    );
  }

  /** One family at an exact version, or the latest when omitted. */
  getTemplate(
    templateId: string,
    params: { version?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<TemplateDto> {
    return this.request<TemplateDto>(
      "GET",
      `/api/templates/${templateId}${qs({ version: params.version })}`,
      undefined,
      options,
    );
  }

  /** Stamps a template onto a new task draft (same create admission). */
  applyTemplate(
    templateId: string,
    request: ApplyTemplateRequest,
  ): Promise<TaskDto> {
    return this.request<TaskDto>(
      "POST",
      `/api/templates/${templateId}/apply`,
      request,
    );
  }

  /** Bounded bulk action (≤100 ids). Idempotency-Key is honored at the
   *  BATCH level: one key reconciles the whole receipt array. */
  bulkTasks(request: BulkTasksRequest): Promise<BulkTasksResponse> {
    return this.request<BulkTasksResponse>("POST", "/api/tasks/bulk", request, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  }

  // -- cycle lifecycle, releases, reports (PKG-W — M2C-04) ---------------

  /** Explicit start: a cycle OPENS on start, not on creation. */
  startCycle(cycleId: string): Promise<CycleDto> {
    return this.request<CycleDto>("POST", `/api/cycles/${cycleId}/start`, {
      idempotency_key: crypto.randomUUID(),
    });
  }

  /** Immutable close snapshot; null while the cycle is open. */
  cycleSnapshot(
    cycleId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CycleSnapshotDto | null> {
    return this.request<CycleSnapshotDto | null>(
      "GET",
      `/api/cycles/${cycleId}/snapshot`,
      undefined,
      options,
    );
  }

  /** Exact authorized cycle report (live under the caller's authority). */
  cycleReport(
    cycleId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CycleReportDto> {
    return this.request<CycleReportDto>(
      "GET",
      `/api/cycles/${cycleId}/report`,
      undefined,
      options,
    );
  }

  /** The workspace's releases (draft + published). */
  listReleases(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ReleaseDto[]> {
    return this.request<ReleaseDto[]>(
      "GET",
      `/api/releases${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  /** Creates a draft release (optional cycle linkage grants nothing). */
  createRelease(request: CreateReleaseRequest): Promise<ReleaseDto> {
    return this.request<ReleaseDto>("POST", "/api/releases", request);
  }

  /**
   * Add-to-release: requires accepted-Done server-side (the accepted
   * gate — review evidence already bound); snapshots issue_key + title.
   */
  addReleaseItem(releaseId: string, taskId: string): Promise<ReleaseItemDto> {
    return this.request<ReleaseItemDto>(
      "POST",
      `/api/releases/${releaseId}/items`,
      {
        task_id: taskId,
        idempotency_key: crypto.randomUUID(),
      },
    );
  }

  /** Publish: draft → published; items accepted → shipped. */
  publishRelease(releaseId: string): Promise<ReleaseDto> {
    return this.request<ReleaseDto>(
      "POST",
      `/api/releases/${releaseId}/publish`,
      {
        idempotency_key: crypto.randomUUID(),
      },
    );
  }

  /** Release report (live aggregates under the caller's authority). */
  releaseReport(
    releaseId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ReleaseReportDto> {
    return this.request<ReleaseReportDto>(
      "GET",
      `/api/releases/${releaseId}/report`,
      undefined,
      options,
    );
  }

  /** Appends a development-evidence link (data only; never review state). */
  addTaskDevEvidence(
    taskId: string,
    request: AddTaskDevEvidenceRequest,
  ): Promise<DevEvidenceDto> {
    return this.request<DevEvidenceDto>(
      "POST",
      `/api/tasks/${taskId}/dev-evidence`,
      request,
    );
  }

  /** Lists a task's development-evidence links. */
  listTaskDevEvidence(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<DevEvidenceDto[]> {
    return this.request<DevEvidenceDto[]>(
      "GET",
      `/api/tasks/${taskId}/dev-evidence`,
      undefined,
      options,
    );
  }

  // -- search -------------------------------------------------------------------

  search(
    params: SearchParams,
    options?: { signal?: AbortSignal },
  ): Promise<SearchResponse> {
    return this.request<SearchResponse>(
      "GET",
      `/api/search${qs({ q: params.q, workspace_id: params.workspace_id, limit: params.limit })}`,
      undefined,
      options,
    );
  }

  // -- agent connections ---------------------------------------------------------

  listAgentConnections(options?: {
    signal?: AbortSignal;
  }): Promise<AgentConnectionDto[]> {
    return this.request<AgentConnectionDto[]>(
      "GET",
      "/api/agent-connections",
      undefined,
      options,
    );
  }

  createAgentConnection(
    request: CreateAgentConnectionRequest,
  ): Promise<AgentConnectionCreatedDto> {
    return this.request<AgentConnectionCreatedDto>(
      "POST",
      "/api/agent-connections",
      request,
    );
  }

  /** Revocation: POST /api/agent-connections/:id/revoke → updated connection. */
  revokeAgentConnection(id: string): Promise<AgentConnectionDto> {
    return this.request<AgentConnectionDto>(
      "POST",
      `/api/agent-connections/${id}/revoke`,
      {},
    );
  }

  // -- wiki: backlinks, history, navigation (PKG-Z) -----------------------------

  /**
   * Pages linking TO this page, each source permission-checked under the
   * caller's grants (an unreadable target is a non-disclosing 404).
   */
  listPageBacklinks(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<BacklinkDto[]> {
    return this.request<BacklinkDto[]>(
      "GET",
      `/api/pages/${pageId}/backlinks`,
      undefined,
      options,
    );
  }

  /** Full revision history, newest first (author + timestamp). */
  listPageHistory(
    pageId: string,
    params: { limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<RevisionSummaryDto[]> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<RevisionSummaryDto[]>(
      "GET",
      `/api/pages/${pageId}/history${search}`,
      undefined,
      options,
    );
  }

  /** View-at-revision: exact immutable reconstruction. */
  getRevision(
    pageId: string,
    revisionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RevisionDto> {
    return this.request<RevisionDto>(
      "GET",
      `/api/pages/${pageId}/revisions/${revisionId}`,
      undefined,
      options,
    );
  }

  /**
   * Restore-to-revision as a NEW head revision (never destructive; the
   * intervening revisions stay immutable and readable).
   */
  restoreRevision(pageId: string, revisionId: string): Promise<SavedDto> {
    return this.request<SavedDto>(
      "POST",
      `/api/pages/${pageId}/restore/${revisionId}`,
      { idempotency_key: crypto.randomUUID() } satisfies RestorePageRequest,
    );
  }

  /** Permission-safe breadcrumb (readable ancestors + permalink aliases). */
  getPagePath(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PagePathDto> {
    return this.request<PagePathDto>(
      "GET",
      `/api/pages/${pageId}/path`,
      undefined,
      options,
    );
  }

  /** Moves a page under a new parent (or the root); links keep resolving. */
  movePage(pageId: string, request: MovePageRequest): Promise<PageSummaryDto> {
    return this.request<PageSummaryDto>(
      "POST",
      `/api/pages/${pageId}/move`,
      request,
    );
  }

  /** Stable permalink resolution (full page-read authorization applies). */
  getPageByAlias(
    alias: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageDto> {
    return this.request<PageDto>(
      "GET",
      `/api/pages/by-alias/${alias}`,
      undefined,
      options,
    );
  }

  // -- project home projection (PKG-AI, M3-03) --------------------------------

  /**
   * The assembled project home: linked issues, classified child pages,
   * activity slice, backlinks and path — one bounded read under the
   * caller's grants (an unreadable page is a non-disclosing 404).
   */
  getProjectHome(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ProjectHomeDto> {
    return this.request<ProjectHomeDto>(
      "GET",
      `/api/pages/${pageId}/project-home`,
      undefined,
      options,
    );
  }

  // -- PKG-AE: diff / trash / export / advisory locks (M3-02) -----------

  /** Structured block-level diff between two immutable revisions. */
  getPageDiff(
    pageId: string,
    params: { from: string; to: string },
    options?: { signal?: AbortSignal },
  ): Promise<PageDiffDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<PageDiffDto>(
      "GET",
      `/api/pages/${pageId}/diff${search}`,
      undefined,
      options,
    );
  }

  /**
   * The workspace trash: ONLY soft-deleted pages the caller could read
   * under the still-live ACL (an unauthorized caller learns nothing).
   */
  listWorkspaceTrash(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TrashEntryDto[]> {
    return this.request<TrashEntryDto[]>(
      "GET",
      `/api/workspaces/${workspaceId}/trash`,
      undefined,
      options,
    );
  }

  /** Soft-deletes (trashes) a page; it keeps its ACL + full history. */
  trashPage(pageId: string): Promise<TrashEntryDto> {
    return this.request<TrashEntryDto>("POST", `/api/pages/${pageId}/trash`, {
      idempotency_key: crypto.randomUUID(),
    } satisfies TrashPageRequest);
  }

  /**
   * Restores a trashed page: re-activates it and commits its
   * deletion-time content as a NEW head revision (never destructive).
   */
  restoreFromTrash(pageId: string): Promise<SavedDto> {
    return this.request<SavedDto>(
      "POST",
      `/api/pages/${pageId}/restore-from-trash`,
      {
        idempotency_key: crypto.randomUUID(),
      } satisfies RestoreFromTrashRequest,
    );
  }

  /** The page's live advisory lock, if any (expired locks read as null). */
  getPageLock(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageLockDto | null> {
    return this.request<PageLockDto | null>(
      "GET",
      `/api/pages/${pageId}/lock`,
      undefined,
      options,
    );
  }

  /** Acquires (or replaces) the advisory lock — warn-on-edit, never an ACL. */
  lockPage(
    pageId: string,
    request: LockPageRequest = {},
  ): Promise<PageLockDto> {
    return this.request<PageLockDto>(
      "POST",
      `/api/pages/${pageId}/lock`,
      request,
    );
  }

  /** Releases the advisory lock (holder's own, or any manager+). */
  unlockPage(pageId: string): Promise<void> {
    return this.request<void>("DELETE", `/api/pages/${pageId}/lock`);
  }

  /**
   * Downloads a page export as text. `markdown` is the deterministic
   * portable projection; `json` is the lossless canonical form. Both run
   * under the caller's read grant; shared-block references export as
   * references, never resolved.
   */
  async exportPage(
    pageId: string,
    format: "markdown" | "json",
  ): Promise<PageExportResult> {
    const response = await this.doFetch(
      `/api/pages/${pageId}/export/${format}`,
      {
        method: "GET",
        headers: {
          Accept: format === "markdown" ? "text/markdown" : "application/json",
        },
        credentials: "include",
      },
    );
    if (!response.ok) {
      let code: string = "unavailable";
      try {
        const text = await response.text();
        if (text) code = (JSON.parse(text) as Partial<ErrorDto>).code ?? code;
      } catch {
        // Non-JSON error body; keep the generic code.
      }
      throw new ApiError(code as ErrorCode, response.status);
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename="([^"]+)"/);
    return {
      filename: match?.[1] ?? `page.${format === "markdown" ? "md" : "json"}`,
      content: await response.text(),
    };
  }

  // ---- PKG-AD: notifications & preferences (M3-02, P20 slice) ---------

  /** The caller's notification preferences (GET /api/me/notification-preferences). */
  getNotificationPreferences(options?: {
    signal?: AbortSignal;
  }): Promise<NotificationPreferencesDto> {
    return this.request<NotificationPreferencesDto>(
      "GET",
      "/api/me/notification-preferences",
      undefined,
      options,
    );
  }

  /**
   * Replaces the caller's notification preferences
   * (PUT /api/me/notification-preferences). Full-form update.
   */
  updateNotificationPreferences(
    request: UpdateNotificationPreferencesRequest,
  ): Promise<NotificationPreferencesDto> {
    return this.request<NotificationPreferencesDto>(
      "PUT",
      "/api/me/notification-preferences",
      request,
    );
  }

  /**
   * The caller's notifications (GET /api/me/notifications): mentions,
   * replies and followed-thread state changes, newest first. Delivery
   * rechecks access per item server-side — a revoked reader's items are
   * absent entirely, never redacted.
   */
  listNotifications(
    params: { limit?: number; cursor?: string } = {},
    options?: { signal?: AbortSignal },
  ): Promise<NotificationsDto> {
    const search = qs(params as Record<string, string | number | undefined>);
    return this.request<NotificationsDto>(
      "GET",
      `/api/me/notifications${search}`,
      undefined,
      options,
    );
  }

  /** Marks one notification read (POST /api/notifications/{id}/read). */
  markNotificationRead(id: string): Promise<NotificationReadDto> {
    return this.request<NotificationReadDto>(
      "POST",
      `/api/notifications/${id}/read`,
      {},
    );
  }

  /**
   * Marks EVERY notification in the caller's inbox read (POST
   * /api/notifications/read-all). Access is rechecked per item exactly as
   * the listing does; idempotent — a replay marks 0.
   */
  markAllNotificationsRead(): Promise<NotificationReadAllDto> {
    return this.request<NotificationReadAllDto>(
      "POST",
      "/api/notifications/read-all",
      {},
    );
  }

  /**
   * The workspace's mentionable principals (GET /api/mentionables):
   * humans AND agent connections, with render labels and the spellable
   * mention tokens the server resolution matches (CORE-90). Workspace
   * admission is the only authority; the listing never grants access.
   * CORE-176: with `pageId` the directory defaults to that page's
   * audience (humans with CURRENT read + agents always) — a composer
   * default filter, not an ACL change.
   */
  listMentionables(
    workspaceId: string,
    options?: { signal?: AbortSignal; pageId?: string },
  ): Promise<MentionablesDto> {
    return this.request<MentionablesDto>(
      "GET",
      `/api/mentionables${qs({ workspace_id: workspaceId, page_id: options?.pageId })}`,
      undefined,
      options,
    );
  }

  /** Resolves a comment thread head (idempotent). */
  resolveCommentThread(commentId: string): Promise<CommentThreadStateDto> {
    return this.request<CommentThreadStateDto>(
      "POST",
      `/api/comments/${commentId}/resolve`,
      {},
    );
  }

  /** Reopens a resolved comment thread (idempotent). */
  reopenCommentThread(commentId: string): Promise<CommentThreadStateDto> {
    return this.request<CommentThreadStateDto>(
      "POST",
      `/api/comments/${commentId}/reopen`,
      {},
    );
  }

  // -- PKG-AC: sharing & guests (M3-02) ---------------------------------------

  /** Lists a page's shares (page readers; the audience is gated metadata). */
  listPageShares(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PageShareDto[]> {
    return this.request<PageShareDto[]>(
      "GET",
      `/api/pages/${pageId}/shares`,
      undefined,
      options,
    );
  }

  /** Creates (or re-issues + rotates) a page share; the link is emailed. */
  createPageShare(request: CreatePageShareRequest): Promise<PageShareDto> {
    return this.request<PageShareDto>(
      "POST",
      `/api/pages/${request.page_id}/shares`,
      request,
    );
  }

  /** Revokes a share (idempotent): the link stops accepting and any grant dies. */
  revokePageShare(pageId: string, shareId: string): Promise<PageShareDto> {
    return this.request<PageShareDto>(
      "POST",
      `/api/pages/${pageId}/shares/${shareId}/revoke`,
      {},
    );
  }

  /** Accepts a share with the authenticated session (token = credential). */
  acceptPageShare(
    request: AcceptPageShareRequest,
  ): Promise<PageShareAcceptedDto> {
    return this.request<PageShareAcceptedDto>(
      "POST",
      "/api/shares/accept",
      request,
    );
  }

  /** The workspace guest listing (managers only). */
  listWorkspaceGuests(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<GuestDto[]> {
    return this.request<GuestDto[]>(
      "GET",
      `/api/workspaces/${workspaceId}/guests`,
      undefined,
      options,
    );
  }

  /** Creates (or re-uses) an access request for one named page. */
  createAccessRequest(
    request: CreateAccessRequestRequest,
  ): Promise<AccessRequestDto> {
    return this.request<AccessRequestDto>(
      "POST",
      `/api/workspaces/${request.workspace_id}/access-requests`,
      request,
    );
  }

  /** Lists a workspace's access requests (managers only). */
  listAccessRequests(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AccessRequestDto[]> {
    return this.request<AccessRequestDto[]>(
      "GET",
      `/api/workspaces/${workspaceId}/access-requests`,
      undefined,
      options,
    );
  }

  /** Approves or denies an access request (one manager mutation). */
  decideAccessRequest(
    requestId: string,
    request: DecideAccessRequestRequest,
  ): Promise<AccessRequestDto> {
    return this.request<AccessRequestDto>(
      "POST",
      `/api/access-requests/${requestId}/decide`,
      request,
    );
  }
  // -- page verification lifecycle (PKG-AH — M3-03) ---------------------------

  /**
   * Verification state + explicit head-vs-verified comparison with source
   * provenance (permission-gated like the page itself).
   */
  getPageVerification(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<VerificationDto> {
    return this.request<VerificationDto>(
      "GET",
      `/api/pages/${pageId}/verification`,
      undefined,
      options,
    );
  }

  /**
   * The currently approved edition: the pinned revision's exact blocks
   * plus provenance. Returns the OLD verified revision, never the head,
   * unless the head IS that revision.
   */
  getVerifiedEdition(
    pageId: string,
    options?: { signal?: AbortSignal },
  ): Promise<VerifiedEditionResponse> {
    return this.request<VerifiedEditionResponse>(
      "GET",
      `/api/pages/${pageId}/verified-edition`,
      undefined,
      options,
    );
  }

  /** Verify a revision (managers+; absent revision_id = current head). */
  verifyPage(
    pageId: string,
    request: VerifyPageRequest,
  ): Promise<VerificationDto> {
    return this.request<VerificationDto>(
      "POST",
      `/api/pages/${pageId}/verify`,
      request,
    );
  }

  /** Withdraw the current approval (managers+; idempotent). */
  withdrawVerification(pageId: string): Promise<VerificationDto> {
    return this.request<VerificationDto>(
      "POST",
      `/api/pages/${pageId}/verification/withdraw`,
      {
        idempotency_key: crypto.randomUUID(),
      } satisfies WithdrawVerificationRequest,
    );
  }

  // -- connectors (CORE-52/74 — workspace-scoped external connections) -----

  /** The workspace's connector connections (manager/admin only). */
  listConnections(
    workspaceId: string,
    params: { limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<ConnectorConnectionDto[]> {
    return this.request<ConnectorConnectionDto[]>(
      "GET",
      `/api/connections${qs({ workspace_id: workspaceId, limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** Creates a connection; the token is encrypted at rest, never echoed. */
  createConnection(
    request: CreateConnectorConnectionRequest,
  ): Promise<ConnectorConnectionDto> {
    return this.request<ConnectorConnectionDto>(
      "POST",
      "/api/connections",
      request,
    );
  }

  /** One connection: status, last sync, latest run receipt. */
  getConnection(
    connectionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConnectorConnectionDto> {
    return this.request<ConnectorConnectionDto>(
      "GET",
      `/api/connections/${connectionId}`,
      undefined,
      options,
    );
  }

  /** Updates name/repo list (exact expected_revision; not the credential). */
  updateConnection(
    connectionId: string,
    request: UpdateConnectorConnectionRequest,
  ): Promise<ConnectorConnectionDto> {
    return this.request<ConnectorConnectionDto>(
      "PATCH",
      `/api/connections/${connectionId}`,
      request,
    );
  }

  /** Revokes: credential destroyed, items deleted, surfacing stops. */
  revokeConnection(
    connectionId: string,
    request: RevokeConnectorConnectionRequest,
  ): Promise<ConnectorConnectionDto> {
    return this.request<ConnectorConnectionDto>(
      "POST",
      `/api/connections/${connectionId}/revoke`,
      request,
    );
  }

  /** Triggers one bounded sync run; returns the receipt. */
  syncConnection(
    connectionId: string,
    request: TriggerConnectorSyncRequest,
  ): Promise<ConnectorSyncRunDto> {
    return this.request<ConnectorSyncRunDto>(
      "POST",
      `/api/connections/${connectionId}/sync`,
      request,
    );
  }

  /** Workspace connector policy (github_auto_transition, default off). */
  getConnectorSettings(
    workspaceId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConnectorWorkspaceSettingsDto> {
    return this.request<ConnectorWorkspaceSettingsDto>(
      "GET",
      `/api/connector-settings${qs({ workspace_id: workspaceId })}`,
      undefined,
      options,
    );
  }

  /** Sets the workspace connector policy (exact expected_revision). */
  setConnectorSettings(
    request: SetConnectorWorkspaceSettingsRequest,
  ): Promise<ConnectorWorkspaceSettingsDto> {
    return this.request<ConnectorWorkspaceSettingsDto>(
      "PUT",
      "/api/connector-settings",
      request,
    );
  }

  /** Pull requests linked to a task (the task's readers only). */
  listTaskLinkedConnectorItems(
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskLinkedConnectorItemsDto> {
    return this.request<TaskLinkedConnectorItemsDto>(
      "GET",
      `/api/tasks/${taskId}/connector-items`,
      undefined,
      options,
    );
  }

  // -- linear import (CORE-240) --

  /** A linear connection's team mappings (manager/admin). */
  getLinearTeamMappings(
    connectionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<LinearTeamMappingsDto> {
    return this.request<LinearTeamMappingsDto>(
      "GET",
      `/api/connections/${connectionId}/linear-mappings`,
      undefined,
      options,
    );
  }

  /** Replaces a linear connection's team mappings wholesale. */
  setLinearTeamMappings(
    request: SetLinearTeamMappingsRequest,
  ): Promise<LinearTeamMappingsDto> {
    return this.request<LinearTeamMappingsDto>(
      "PUT",
      `/api/connections/${request.connection_id}/linear-mappings`,
      request,
    );
  }

  /** Runs (or resumes) a linear import; returns the batch report. */
  triggerLinearImport(
    connectionId: string,
    request: TriggerLinearImportRequest,
  ): Promise<LinearImportRunDto> {
    return this.request<LinearImportRunDto>(
      "POST",
      `/api/connections/${connectionId}/import`,
      request,
    );
  }

  /** CORE-241: runs one outbound push pass (policy-gated, claims
   * before the wire, echo-aware); returns the run receipt. */
  triggerLinearPush(
    connectionId: string,
    request: TriggerLinearPushRequest,
  ): Promise<LinearPushRunDto> {
    return this.request<LinearPushRunDto>(
      "POST",
      `/api/connections/${connectionId}/push`,
      request,
    );
  }

  /** CORE-241: runs one reconciliation pass (repairs safe drift,
   * reports the unsafe); returns the run receipt. */
  triggerLinearReconcile(
    connectionId: string,
    request: TriggerLinearReconcileRequest,
  ): Promise<LinearReconcileRunDto> {
    return this.request<LinearReconcileRunDto>(
      "POST",
      `/api/connections/${connectionId}/reconcile`,
      request,
    );
  }

  // -- integrations (CORE-125/182 — project-owned ingest webhook endpoints)

  /** The workspace's cross-project endpoint ROLLUP (Read; no secrets). */
  listIntegrationEndpoints(
    workspaceId: string,
    params: { limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<IntegrationEndpointListDto> {
    return this.request<IntegrationEndpointListDto>(
      "GET",
      `/api/workspaces/${workspaceId}/integration-endpoints${qs({ limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** One project's ingest endpoints (project-scoped Read). */
  listProjectIntegrationEndpoints(
    projectId: string,
    params: { limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<IntegrationEndpointListDto> {
    return this.request<IntegrationEndpointListDto>(
      "GET",
      `/api/projects/${projectId}/integration-endpoints${qs({ limit: params.limit })}`,
      undefined,
      options,
    );
  }

  /** Creates an endpoint on a project; token + secret return EXACTLY ONCE. */
  createIntegrationEndpoint(
    request: CreateIntegrationEndpointRequest,
  ): Promise<IntegrationEndpointSecretDto> {
    return this.request<IntegrationEndpointSecretDto>(
      "POST",
      "/api/projects/" +
        encodeURIComponent(request.project_id) +
        "/integration-endpoints",
      request,
    );
  }

  /** Rotates the secret; the OLD secret refuses by name from the commit. */
  rotateIntegrationEndpointSecret(
    endpointId: string,
    request: RotateIntegrationEndpointSecretRequest,
  ): Promise<IntegrationEndpointSecretDto> {
    return this.request<IntegrationEndpointSecretDto>(
      "POST",
      `/api/integration-endpoints/${endpointId}/rotate-secret`,
      request,
    );
  }

  /** Pauses (arrivals refuse by name) or resumes an endpoint. */
  setIntegrationEndpointStatus(
    endpointId: string,
    request: SetIntegrationEndpointStatusRequest,
  ): Promise<IntegrationEndpointDto> {
    return this.request<IntegrationEndpointDto>(
      "POST",
      `/api/integration-endpoints/${endpointId}/status`,
      request,
    );
  }

  /** Deletes an endpoint; the ledger keeps its recorded reasons. */
  deleteIntegrationEndpoint(endpointId: string): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/integration-endpoints/${endpointId}`,
    );
  }

  /** The endpoint's webhook delivery ledger (every arrival explained). */
  listIntegrationEndpointDeliveries(
    endpointId: string,
    params: { status?: string; limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<IntakeDeliveryListDto> {
    return this.request<IntakeDeliveryListDto>(
      "GET",
      `/api/integration-endpoints/${endpointId}/intake-deliveries${qs({
        status: params.status,
        limit: params.limit,
      })}`,
      undefined,
      options,
    );
  }
}
